import { BackArrowIcon } from "@/assets/icons/admin";
import { Button, Input } from "@nextui-org/react";
import { Switch } from "@headlessui/react";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { BannerUpload } from "@/components/admin/cmsmodule/heroBanner/bannerconfig/BannerUpload.tsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon } from "@heroicons/react/24/solid";
import {
  Banner,
  ShowType,
  useUpdateBanners,
  useCreateBanner,
  BannerStatus,
} from "@/services/banner.service.ts";

interface BannerFormProps {
  itemData?: Banner;
  onCancel: () => void;
}

const BannerForm: React.FC<BannerFormProps> = ({ itemData, onCancel }) => {
  const isEditMode = !!itemData; // Check if editing or creating
  const [name, setName] = useState(itemData?.name || "");
  const [showType] = useState(itemData?.showType || ShowType.SCHEDULED);
  const [isDisplay, setIsDisplay] = useState(itemData?.isDisplay || false);
  const [bannerUrl, setBannerUrl] = useState("");
  const [desktopUrl, setDesktopUrl] = useState(itemData?.desktopUrl || "");
  const [mobileUrl, setMobileUrl] = useState(itemData?.mobileUrl || "");
  const [startDate, setStartDate] = useState(
    itemData?.startDate ? new Date(itemData.startDate) : new Date(),
  );
  const [endDate, setEndDate] = useState(
    itemData?.endDate ? new Date(itemData.endDate) : new Date(),
  );

  const editBanners = useUpdateBanners();
  const createBanner = useCreateBanner();

  const handleSave = () => {
    if (!name?.trim()) {
      toast.error("Name is required");
      return;
    }

    if (showType === ShowType.SCHEDULED) {
      if (!startDate || !endDate) {
        toast.error("Start and end dates are required");
        return;
      }

      if (startDate.getTime() === endDate.getTime()) {
        toast.error("Start and end dates cannot be the same");
        return;
      }

      if (endDate < startDate) {
        toast.error("End date cannot be before start date");
        return;
      }
    }

    const bannerData: Banner = {
      id: itemData?.id ?? "", // If editing, keep ID; otherwise, leave empty
      name,
      showType,
      desktopUrl: desktopUrl ?? bannerUrl,
      mobileUrl: mobileUrl ?? bannerUrl,
      startDate:
        showType === ShowType.SCHEDULED ? startDate.toISOString() : undefined,
      endDate:
        showType === ShowType.SCHEDULED ? endDate.toISOString() : undefined,
      isDisplay: showType === ShowType.ALL_TIME ? isDisplay : undefined,
    };

    if (isEditMode) {
      editBanners.mutate([bannerData], {
        onSuccess: () => toast.success("Banner updated successfully"),
        onError: (error) => console.error("Failed to update banner:", error),
      });
    } else {
      createBanner.mutate(bannerData, {
        onSuccess: () => toast.success("Banner created successfully"),
        onError: (error) => console.error("Failed to create banner:", error),
      });
    }
  };

  return (
    <div className="shadow-lg mx-auto w-full text-white">
      <div className="mb-2 flex items-center">
        <Button isIconOnly onPress={onCancel} className="text-white">
          <BackArrowIcon />
        </Button>
        <h2 className="text-xl font-semibold">
          {isEditMode ? "Edit Banner" : "Add Banner"}
        </h2>
      </div>
      {isEditMode && (
        <div className="flex items-center justify-between rounded-lg bg-[#021557] p-3 text-white">
          <span className="pl-4 text-lg font-semibold">
            Slide {itemData.orderNumber}
          </span>
          {itemData.status == BannerStatus.SHOWING && (
            <div className="flex items-center font-semibold text-yellow-400">
              <span className="mr-2">⚠️</span>
              <span>This banner is showing</span>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-6">
        <div>
          <label className="mb-2 block text-sm">Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-gray-600 bg-[#20244c] text-white"
          />
        </div>
      </div>

      {/* Image Uploads */}
      <div className="my-6 grid grid-cols-2 gap-6">
        <BannerUpload
          imageUrl={desktopUrl}
          onUpload={setDesktopUrl}
          label="Desktop Banner Image"
          dimensions="1200x454 px"
        />
        <BannerUpload
          imageUrl={mobileUrl}
          onUpload={setMobileUrl}
          label="Mobile Banner Image"
          dimensions="345x219 px"
        />
      </div>

      {/* Form Inputs */}
      <div className="flex flex-col gap-6">
        <div>
          <label className="mb-2 block text-sm">Banner URL (Optional)</label>
          <Input
            value={bannerUrl}
            onChange={(e) => setBannerUrl(e.target.value)}
            className="w-full rounded-md border border-gray-600 bg-[#20244c] text-white"
          />
        </div>

        {/* Scheduled Type */}
        {showType === ShowType.SCHEDULED && (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="mb-2 block text-sm">Start Date and Time</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date as Date)}
                showTimeSelect
                timeFormat="HH:mm:ss"
                dateFormat="dd/MM/yyyy HH:mm:ss"
                className="w-full rounded-md border border-gray-600 bg-[#20244c] p-2 text-white"
              />
              <CalendarIcon className="absolute right-3 top-3 h-5 w-5 text-white" />
            </div>
            <div>
              <label className="mb-2 block text-sm">End Date and Time</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date as Date)}
                showTimeSelect
                timeFormat="HH:mm:ss"
                dateFormat="dd/MM/yyyy HH:mm:ss"
                className="w-full rounded-md border border-gray-600 bg-[#20244c] p-2 text-white"
              />
              <CalendarIcon className="absolute right-3 top-3 h-5 w-5 text-white" />
            </div>
          </div>
        )}

        {/* All Time Type */}
        {showType === ShowType.ALL_TIME && (
          <div className="grid grid-cols-1">
            <label className="mb-2 block text-sm">Show Banner</label>
            <Switch
              checked={isDisplay}
              onChange={setIsDisplay}
              className={`${isDisplay ? "bg-[#0083C9]" : "bg-gray-600"} relative inline-flex h-6 w-12 items-center rounded-full`}
            >
              <span
                className={`${isDisplay ? "translate-x-6" : "translate-x-0"} inline-block h-5 w-5 transform rounded-full bg-white transition`}
              />
            </Switch>
            <p className="mt-2 text-sm text-gray-400">
              Banner will {isDisplay ? "be shown" : "not be shown"} when the
              toggle is {isDisplay ? "on" : "off"} and after changes are saved.
            </p>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex w-full justify-end gap-4 px-6 py-4">
          <Button
            onPress={onCancel}
            className="rounded-md border border-[#4A4F77] px-6 py-2 text-[#A6A9EB] transition-all hover:bg-[#1C2141]"
          >
            Cancel
          </Button>
          <Button
            onPress={handleSave}
            className="rounded-md bg-gradient-to-r from-[#2E65FF] to-[#003BDA] px-6 py-2 text-white transition-all hover:from-[#1F4ADF] hover:to-[#002DBB]"
          >
            {isEditMode ? "Save Changes" : "Add Banner"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BannerForm;
